import React, { useState } from "react";
import { Formik } from "formik";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { FormattedMessage, injectIntl } from "react-intl";
import { UserPlusIcon, ExclamationCircleIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import * as auth from "../../store/ducks/auth.duck";
import { register } from "../../crud/auth.crud";

function Registration(props) {
  const { intl } = props;
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  return (
    <div className="tw-fixed tw-inset-0 tw-flex tw-overflow-hidden">
      {/* Left Side - Background Image with Logo and Text */}
      <div
        className="tw-hidden lg:tw-flex tw-flex-col tw-justify-between tw-w-1/2 tw-bg-cover tw-bg-center tw-bg-no-repeat tw-p-12 tw-relative"
        style={{
          backgroundImage: 'url(/media/bg/logindsa.jpg)',
        }}
      >
        {/* Dark overlay */}
        <div className="tw-absolute tw-inset-0 tw-bg-black/40"></div>

        {/* Content */}
        <div className="tw-relative tw-z-10">
          <img
            src="/media/logos/logo-4.png"
            alt="ITP Logo"
            style={{ width: '100px' }}
            className="tw-mb-8"
          />
        </div>

        <div className="tw-relative tw-z-10">
          <h3 className="tw-text-3xl tw-font-bold tw-text-white tw-mb-4">
            Welcome to ITP!
          </h3>
          <p className="tw-text-white/90 tw-text-lg tw-leading-relaxed">
            By logging in you agree to follow all ITP company policies and procedures. Your activity may be monitored.
          </p>
        </div>

        <div className="tw-relative tw-z-10">
          <p className="tw-text-white/80 tw-text-sm">
            ITP &copy; {new Date().getFullYear()}
          </p>
        </div>
      </div>

      {/* Right Side - Registration Form */}
      <div className="tw-w-full lg:tw-w-1/2 tw-flex tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-blue-600 tw-to-indigo-700 dark:tw-from-gray-900 dark:tw-to-gray-800 tw-overflow-auto">
        <div className="tw-max-w-md tw-w-full tw-px-8 tw-py-12">
          {/* Logo - always visible */}
          <div className="tw-text-center tw-mb-8">
            <img
              src="/media/logos/logo-4.png"
              alt="ITP Logo"
              style={{ width: '100px' }}
              className="tw-mx-auto tw-mb-4"
            />
            <h2 className="tw-text-2xl tw-font-bold tw-text-white">
              <FormattedMessage id="AUTH.REGISTER.TITLE" />
            </h2>
            <p className="tw-mt-2 tw-text-sm tw-text-white/90 dark:tw-text-gray-300">
              Enter your details to create your account
            </p>
          </div>

          {/* Registration Card */}
          <div className="tw-bg-white dark:tw-bg-gray-800 tw-rounded-2xl tw-shadow-xl tw-p-8">
            <Formik
              initialValues={{
                email: "",
                fullname: "",
                username: "",
                password: "",
                acceptTerms: true,
                confirmPassword: "",
              }}
              validate={(values) => {
                const errors = {};

                if (!values.email) {
                  errors.email = intl.formatMessage({
                    id: "AUTH.VALIDATION.REQUIRED_FIELD",
                  });
                } else if (
                  !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
                ) {
                  errors.email = intl.formatMessage({
                    id: "AUTH.VALIDATION.INVALID_FIELD",
                  });
                }

                if (!values.fullname) {
                  errors.fullname = intl.formatMessage({
                    id: "AUTH.VALIDATION.REQUIRED_FIELD",
                  });
                }

                if (!values.username) {
                  errors.username = intl.formatMessage({
                    id: "AUTH.VALIDATION.REQUIRED_FIELD",
                  });
                }

                if (!values.password) {
                  errors.password = intl.formatMessage({
                    id: "AUTH.VALIDATION.REQUIRED_FIELD",
                  });
                }

                if (!values.confirmPassword) {
                  errors.confirmPassword = intl.formatMessage({
                    id: "AUTH.VALIDATION.REQUIRED_FIELD",
                  });
                } else if (values.password !== values.confirmPassword) {
                  errors.confirmPassword =
                    "Password and Confirm Password didn't match.";
                }

                if (!values.acceptTerms) {
                  errors.acceptTerms = "Accept Terms";
                }

                return errors;
              }}
              onSubmit={(values, { setStatus, setSubmitting }) => {
                setLoading(true);
                setErrorMessage("");
                register(
                  values.email,
                  values.fullname,
                  values.username,
                  values.password
                )
                  .then(({ data: { accessToken } }) => {
                    setLoading(false);
                    props.register(accessToken);
                  })
                  .catch(() => {
                    setLoading(false);
                    setSubmitting(false);
                    setErrorMessage("Registration failed. Please try again.");
                  });
              }}
            >
              {({
                values,
                status,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit} className="tw-space-y-5">
                  {/* Error Alert */}
                  {(errorMessage || status) && (
                    <div className="tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-800 tw-rounded-lg tw-p-4 tw-flex tw-items-start tw-gap-3">
                      <ExclamationCircleIcon className="tw-h-5 tw-w-5 tw-text-red-600 dark:tw-text-red-400 tw-flex-shrink-0 tw-mt-0.5" />
                      <p className="tw-text-sm tw-text-red-700 dark:tw-text-red-400">
                        {errorMessage || status}
                      </p>
                    </div>
                  )}

                  {/* Fullname Field */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="fullname"
                      value={values.fullname}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`tw-block tw-w-full tw-px-3 tw-py-3 tw-border tw-rounded-lg tw-bg-white dark:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-white placeholder:tw-text-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-transition-colors ${
                        touched.fullname && errors.fullname
                          ? "tw-border-red-300 dark:tw-border-red-700"
                          : "tw-border-gray-300 dark:tw-border-gray-600"
                      }`}
                      placeholder="John Doe"
                    />
                    {touched.fullname && errors.fullname && (
                      <p className="tw-mt-1 tw-text-sm tw-text-red-600 dark:tw-text-red-400">
                        {errors.fullname}
                      </p>
                    )}
                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`tw-block tw-w-full tw-px-3 tw-py-3 tw-border tw-rounded-lg tw-bg-white dark:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-white placeholder:tw-text-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-transition-colors ${
                        touched.email && errors.email
                          ? "tw-border-red-300 dark:tw-border-red-700"
                          : "tw-border-gray-300 dark:tw-border-gray-600"
                      }`}
                      placeholder="you@example.com"
                    />
                    {touched.email && errors.email && (
                      <p className="tw-mt-1 tw-text-sm tw-text-red-600 dark:tw-text-red-400">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Username Field */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      name="username"
                      value={values.username}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`tw-block tw-w-full tw-px-3 tw-py-3 tw-border tw-rounded-lg tw-bg-white dark:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-white placeholder:tw-text-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-transition-colors ${
                        touched.username && errors.username
                          ? "tw-border-red-300 dark:tw-border-red-700"
                          : "tw-border-gray-300 dark:tw-border-gray-600"
                      }`}
                      placeholder="johndoe"
                    />
                    {touched.username && errors.username && (
                      <p className="tw-mt-1 tw-text-sm tw-text-red-600 dark:tw-text-red-400">
                        {errors.username}
                      </p>
                    )}
                  </div>

                  {/* Password Field */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Password
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`tw-block tw-w-full tw-px-3 tw-py-3 tw-border tw-rounded-lg tw-bg-white dark:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-white placeholder:tw-text-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-transition-colors ${
                        touched.password && errors.password
                          ? "tw-border-red-300 dark:tw-border-red-700"
                          : "tw-border-gray-300 dark:tw-border-gray-600"
                      }`}
                      placeholder="••••••••"
                    />
                    {touched.password && errors.password && (
                      <p className="tw-mt-1 tw-text-sm tw-text-red-600 dark:tw-text-red-400">
                        {errors.password}
                      </p>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`tw-block tw-w-full tw-px-3 tw-py-3 tw-border tw-rounded-lg tw-bg-white dark:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-white placeholder:tw-text-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-transition-colors ${
                        touched.confirmPassword && errors.confirmPassword
                          ? "tw-border-red-300 dark:tw-border-red-700"
                          : "tw-border-gray-300 dark:tw-border-gray-600"
                      }`}
                      placeholder="••••••••"
                    />
                    {touched.confirmPassword && errors.confirmPassword && (
                      <p className="tw-mt-1 tw-text-sm tw-text-red-600 dark:tw-text-red-400">
                        {errors.confirmPassword}
                      </p>
                    )}
                  </div>

                  {/* Terms Checkbox */}
                  <div className="tw-flex tw-items-start tw-gap-2">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      checked={values.acceptTerms}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="tw-mt-1 tw-h-4 tw-w-4 tw-rounded tw-border-gray-300 tw-text-blue-600 focus:tw-ring-blue-500"
                    />
                    <label className="tw-text-sm tw-text-gray-600 dark:tw-text-gray-400">
                      I agree to the{" "}
                      <Link
                        to="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw-text-blue-600 hover:tw-text-blue-500 dark:tw-text-blue-400 tw-underline"
                      >
                        Terms & Conditions
                      </Link>
                    </label>
                  </div>
                  {touched.acceptTerms && errors.acceptTerms && (
                    <p className="tw-text-sm tw-text-red-600 dark:tw-text-red-400">
                      {errors.acceptTerms}
                    </p>
                  )}

                  {/* Action Buttons */}
                  <div className="tw-flex tw-flex-col tw-gap-3 tw-pt-2">
                    <button
                      type="submit"
                      disabled={isSubmitting || loading || !values.acceptTerms}
                      className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-lg tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-font-semibold tw-shadow-lg hover:tw-shadow-xl tw-transition-all disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <svg
                            className="tw-animate-spin tw-h-5 tw-w-5 tw-text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="tw-opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="tw-opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>Creating Account...</span>
                        </>
                      ) : (
                        <>
                          <UserPlusIcon className="tw-h-5 tw-w-5" />
                          <span>Create Account</span>
                        </>
                      )}
                    </button>

                    <Link
                      to="/auth/login"
                      className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-lg tw-bg-gray-100 hover:tw-bg-gray-200 dark:tw-bg-gray-700 dark:hover:tw-bg-gray-600 tw-text-gray-700 dark:tw-text-gray-300 tw-font-semibold tw-transition-all"
                    >
                      <ArrowLeftIcon className="tw-h-5 tw-w-5" />
                      <span>Back to Login</span>
                    </Link>
                  </div>
                </form>
              )}
            </Formik>
          </div>

          {/* Footer */}
          <p className="tw-mt-6 tw-text-center tw-text-sm tw-text-white/80 dark:tw-text-gray-400 lg:tw-hidden">
            ITP &copy; {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
}

export default injectIntl(
  connect(
    null,
    auth.actions
  )(Registration)
);
